/**
 * Nav redesign Oct 2026 (W6): desktop Wristbands mega menu (snippets/desktop-wristbands-mega.liquid).
 *
 * - Hover intent: opens after 120ms over the trigger / panel, closes 300ms after the pointer leaves both
 *   (re-entering cancels the close). Mouse only; touch uses click.
 * - Click / Enter / Space on the trigger toggles the panel and never navigates (href is the no-JS fallback).
 * - Material tabs (WAI-ARIA vertical tabs, automatic activation): click or focus switches instantly;
 *   Up / Down / Home / End move between tabs. Pointer hover switches with "menu aim": while the pointer is
 *   heading diagonally into the content panel, tabs it crosses are ignored until it pauses (75ms).
 * - Escape closes from anywhere inside and returns focus to the trigger; focus or a click leaving the item
 *   closes it; opening closes any other open desktop dropdown.
 * - Desktop only (min-width: 1200px). Open / close is the existing `.dropdown.open` class (global-header.css).
 */
(function () {
  const DESKTOP = window.matchMedia('(min-width: 1200px)');
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 300;
  const AIM_DELAY = 75;
  const AIM_TOLERANCE = 40; // px added above / below the content panel for the aim triangle
  const CLICK_AFTER_HOVER_GUARD = 400; // ms: a click right after a hover-open must not close it again

  class WristbandsMega {
    constructor(root) {
      this.root = root;
      this.trigger = root.querySelector('[data-wristbands-mega-trigger]');
      this.content = root.querySelector('[data-wristbands-mega-content]');
      this.tabs = Array.from(root.querySelectorAll('[data-wristbands-mega-tab]'));
      if (!this.trigger || !this.content || !this.tabs.length) return;

      this.openTimer = null;
      this.closeTimer = null;
      this.aimTimer = null;
      this.hoverTab = null;
      this.pointerTrail = [];
      this.openedByHoverAt = 0;

      this.bindTrigger();
      this.bindHover();
      this.bindTabs();
      this.bindDismiss();
      this.syncWithClass();
    }

    isOpen() {
      return this.root.classList.contains('open');
    }

    open(reason) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.isOpen()) return;
      this.closeOtherDropdowns();
      this.root.classList.add('open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.openedByHoverAt = reason === 'hover' ? Date.now() : 0;
    }

    close({ returnFocus = false } = {}) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.aimTimer);
      if (this.isOpen()) {
        this.root.classList.remove('open');
        this.trigger.setAttribute('aria-expanded', 'false');
      }
      if (returnFocus) this.trigger.focus();
    }

    /* Other desktop dropdowns (Shop By Use, Resources) use global.js Dropdown and the same `open` class */
    closeOtherDropdowns() {
      const nav = this.root.parentElement;
      if (!nav) return;
      nav.querySelectorAll(':scope > .dropdown.open').forEach((item) => {
        if (item === this.root) return;
        item.classList.remove('open');
        const toggle = item.querySelector('[aria-expanded]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    }

    bindTrigger() {
      this.trigger.addEventListener('click', (event) => {
        if (!DESKTOP.matches) return;
        event.preventDefault();
        if (this.isOpen()) {
          if (this.openedByHoverAt && Date.now() - this.openedByHoverAt < CLICK_AFTER_HOVER_GUARD) return;
          this.close();
        } else {
          this.open('click');
        }
      });
      // role="button" on a link: Space must activate it too (Enter already fires click)
      this.trigger.addEventListener('keydown', (event) => {
        if (!DESKTOP.matches || event.key !== ' ') return;
        event.preventDefault();
        this.trigger.click();
      });
    }

    bindHover() {
      this.root.addEventListener('pointerenter', (event) => {
        if (!DESKTOP.matches || event.pointerType !== 'mouse') return;
        clearTimeout(this.closeTimer);
        if (!this.isOpen()) this.openTimer = setTimeout(() => this.open('hover'), OPEN_DELAY);
      });
      this.root.addEventListener('pointerleave', (event) => {
        if (!DESKTOP.matches || event.pointerType !== 'mouse') return;
        clearTimeout(this.openTimer);
        clearTimeout(this.aimTimer);
        if (this.isOpen()) this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
      });
      // Moving onto another top-level item closes this panel at once (no overlap with its dropdown)
      const nav = this.root.parentElement;
      if (nav) {
        nav.addEventListener('pointerover', (event) => {
          if (!DESKTOP.matches || event.pointerType !== 'mouse' || !this.isOpen()) return;
          const item = event.target.closest('.navbar-nav > li');
          if (item && item !== this.root) this.close();
        });
      }
    }

    bindTabs() {
      const list = this.tabs[0].closest('[role="tablist"]');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('focus', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          const last = this.tabs.length - 1;
          let next = null;
          if (event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
          else if (event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = last;
          if (next === null) return;
          event.preventDefault();
          this.tabs[next].focus(); // focus handler selects it
        });
      });

      // Pointer: menu aim
      if (!list) return;
      list.addEventListener('mousemove', (event) => {
        this.trackPointer(event);
        const tab = event.target.closest('[data-wristbands-mega-tab]');
        clearTimeout(this.aimTimer);
        this.hoverTab = tab;
        if (!tab || tab.classList.contains('is-active')) return;
        if (this.isAimingAtContent()) {
          // Heading for the content: switch only if the pointer pauses here
          this.aimTimer = setTimeout(() => {
            if (this.hoverTab === tab) this.select(this.tabs.indexOf(tab));
          }, AIM_DELAY);
        } else {
          this.select(this.tabs.indexOf(tab));
        }
      });
      list.addEventListener('mouseleave', () => {
        this.hoverTab = null;
        clearTimeout(this.aimTimer);
      });
    }

    trackPointer(event) {
      this.pointerTrail.push({ x: event.clientX, y: event.clientY });
      if (this.pointerTrail.length > 4) this.pointerTrail.shift();
    }

    /* True when the pointer's recent movement points into the content panel: the current position lies in
       the triangle from an earlier position to the panel's left edge (top and bottom, plus tolerance). */
    isAimingAtContent() {
      if (this.pointerTrail.length < 2) return false;
      const from = this.pointerTrail[0];
      const to = this.pointerTrail[this.pointerTrail.length - 1];
      const rect = this.content.getBoundingClientRect();
      if (to.x <= from.x) return false; // not moving right, toward the panel
      const top = { x: rect.left, y: rect.top - AIM_TOLERANCE };
      const bottom = { x: rect.left, y: rect.bottom + AIM_TOLERANCE };
      return pointInTriangle(to, from, top, bottom);
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
      });
    }

    bindDismiss() {
      this.root.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen()) return;
        event.preventDefault();
        event.stopPropagation();
        this.close({ returnFocus: true });
      });
      // Tabbing past the last element (or back out before the trigger) closes it
      this.root.addEventListener('focusout', (event) => {
        if (!DESKTOP.matches) return;
        // No relatedTarget: click on non-focusable space or window blur; outside clicks are handled below
        if (!event.relatedTarget || this.root.contains(event.relatedTarget)) return;
        this.close();
      });
      document.addEventListener('click', (event) => {
        if (this.isOpen() && !this.root.contains(event.target)) this.close();
      });
      DESKTOP.addEventListener('change', () => {
        if (!DESKTOP.matches) this.close();
      });
    }

    /* Other scripts may remove `open` (e.g. global.js closing dropdowns on a plain menu item):
       keep aria-expanded and timers in step with the class. */
    syncWithClass() {
      new MutationObserver(() => {
        if (!this.isOpen() && this.trigger.getAttribute('aria-expanded') === 'true') {
          this.trigger.setAttribute('aria-expanded', 'false');
          clearTimeout(this.closeTimer);
          clearTimeout(this.aimTimer);
        }
      }).observe(this.root, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function pointInTriangle(p, a, b, c) {
    const sign = (p1, p2, p3) => (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
    const d1 = sign(p, a, b);
    const d2 = sign(p, b, c);
    const d3 = sign(p, c, a);
    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
    return !(hasNeg && hasPos);
  }

  document.querySelectorAll('[data-wristbands-mega]').forEach((root) => new WristbandsMega(root));
})();
